import {
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import PageHeader from '@modules/project/component/page-header/PageHeader';
import Summary from '@modules/project/component/summary/Summary';
import DetailsMobile from '@modules/project/component/details/Details.mobile';
import ContactsMobile from '@modules/project/component/contacts/Contacts.mobile';
import Awards from '@modules/project/component/awards/Awards';
import Details from '@modules/project/component/details/Details';
import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '~/modules/design-board';
import { HoverAction } from '~/modules/page-builder/component/common/hover-action/HoverAction';
import * as S from './ProjectPageHeader.style';
import { ProjectEnquiryForm } from '@modules/project/component/project-enquiry/ProjectEnquiryForm';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { getEnquiryMessagePlaceholder } from '~/modules/enquiry/util/get-enquiry-message-placeholder';

interface ProjectPageHeaderProps {
    canDownloadBannerImage: boolean;
}

const ProjectPageHeader = ({
    canDownloadBannerImage,
}: ProjectPageHeaderProps) => {
    const { primaryCTA } = useLoaderData<typeof loader>();
    const project = useProjectPreviewRouteData();

    const { desktop } = useAppDisplayModeContext();

    const theme = useTheme();
    const { css } = useStyles();

    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });

    const bannerDimensions = getBannerDimensions('project');

    const { favouriteItem } = useSaveToDesignBoard('ProjectPin', project.ID);

    const messageBodyPlaceholder = getEnquiryMessagePlaceholder(
        project.Professional.Title,
        'project'
    );

    const banner = (
        <DetailBannerSection
            {...project.Banner}
            title={project.Title}
            dimensions={bannerDimensions}
            size={bannerSize}
            showBackButton={false}
            showActionsOnHover={true}
            awards={project.Awards}
            actions={
                desktop
                    ? [
                          <div
                              key="bannerGrid"
                              className={css(S.BannerActions)}
                          >
                              <DownloadAction
                                  key="downloadAction"
                                  canDownload={canDownloadBannerImage}
                                  downloadLink={
                                      project.Banner.downloadLink ?? ''
                                  }
                                  libraryImageID={
                                      project.Banner.libraryImage?.ID ?? 0
                                  }
                              />
                              <HoverAction
                                  key="saveToDesignBoard"
                                  label="Save to design board"
                                  action={
                                      <SaveToDesignBoard
                                          {...favouriteItem}
                                          heartButton={{
                                              size: 24,
                                              variables: {
                                                  buttonRadius: '50%',
                                                  buttonPadding: pxToRem(12),
                                                  iconPadding: 0,
                                                  primaryNoBKGButtonBackgroundColorNormal:
                                                      theme.siteVariables.colors
                                                          .white[300],
                                                  primaryNoBKGButtonBackgroundColorHover:
                                                      theme.siteVariables.colors
                                                          .white[250],
                                              },
                                          }}
                                      />
                                  }
                              />
                          </div>,
                      ]
                    : []
            }
        />
    );

    return (
        <>
            {desktop ? (
                <>
                    {project.Banner && banner}
                    <Details data={project} />
                    {project.Content && <Summary data={project} />}
                    {project.Awards.length !== 0 && (
                        <Awards awards={project.Awards} />
                    )}
                    <ProjectEnquiryForm
                        data={{
                            Professional: {
                                ...project.Professional,
                                Website: project.Professional.Website,
                                primaryCTA,
                            },
                            website: project.Website,
                            siteTreeID: project.ID,
                            messageBodyPlaceholder,
                            primaryCTA,
                            itemType: 'project',
                        }}
                    />
                </>
            ) : (
                <>
                    {project.Banner && banner}
                    <PageHeader data={project} />
                    {project.Content && <Summary data={project} />}
                    <DetailsMobile data={project} />
                    <ContactsMobile data={project} />
                    {project.Awards.length !== 0 && (
                        <Awards awards={project.Awards} />
                    )}
                </>
            )}
        </>
    );
};
export default ProjectPageHeader;
